import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { HandHeart, ShieldAlert, AlertTriangle, Phone, Filter, ChevronDown, ChevronUp, Save } from "lucide-react";
import { toast } from "sonner";

const CONCERN_LABEL = {
  immediate_safety: "Immediate safety / abuse",
  neglect_isolation: "Neglect or isolation",
  medical_mobility: "Medical / mobility",
  financial_concern: "Financial",
  food_utilities: "Food / utilities",
  housing: "Housing",
  spiritual_loneliness: "Spiritual loneliness",
  transportation: "Transportation",
};

const URGENCY_LABEL = {
  immediate: "Immediate",
  this_week: "This week",
  ongoing: "Ongoing",
};

const STATUS_OPTIONS = [
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In progress" },
  { key: "resolved", label: "Resolved" },
  { key: "escalated", label: "Escalated" },
];

const STATUS_BADGE = {
  open: "bg-burgundy/10 text-burgundy",
  in_progress: "bg-gold/20 text-burgundy",
  resolved: "bg-jjblue/10 text-jjblue/80",
  escalated: "bg-burgundy text-cream",
};

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function ReportCard({ r, onUpdate }) {
  const [open, setOpen] = useState(r.status === "open" || r.urgency === "immediate");
  const [status, setStatus] = useState(r.status);
  const [notes, setNotes] = useState(r.admin_notes || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/elder-reports/${r.id}`, { status, admin_notes: notes });
      onUpdate({ ...r, ...data });
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const dirty = status !== r.status || (notes || "") !== (r.admin_notes || "");
  const where = [r.elder_city, r.elder_state].filter(Boolean).join(", ");

  return (
    <li className="jj-card bg-white p-4" data-testid={`elder-report-${r.id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-3 text-left"
        data-testid={`elder-report-toggle-${r.id}`}
      >
        <div className="h-9 w-9 rounded-xl bg-burgundy/10 text-burgundy flex items-center justify-center shrink-0">
          {r.urgency === "immediate" ? <AlertTriangle className="h-4 w-4" strokeWidth={1.7} /> : <HandHeart className="h-4 w-4" strokeWidth={1.7} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-jjblue truncate max-w-[18rem]">{r.elder_name}</span>
            {r.elder_age ? <span className="text-[11px] text-[var(--jj-muted)]">~{r.elder_age}</span> : null}
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_BADGE[r.status] || "bg-jj/10"}`} data-testid={`elder-report-status-${r.id}`}>
              {r.status.replace("_", " ")}
            </span>
            {r.urgency === "immediate" && (
              <span className="text-[10px] uppercase tracking-wider bg-burgundy text-cream px-2 py-0.5 rounded-full">urgent</span>
            )}
            <span className="text-[11px] text-[var(--jj-muted)]">{URGENCY_LABEL[r.urgency] || r.urgency}</span>
            <span className="text-[11px] text-[var(--jj-muted)]">· {timeAgo(r.created_at)}</span>
          </div>
          <div className="mt-1 text-xs text-[var(--jj-muted)] flex items-center gap-2 flex-wrap">
            {where && <span>{where}</span>}
            {r.relationship && <span>· {r.relationship}</span>}
            <span>· flagged by{" "}
              <Link
                to={`/u/${r.reporter_id}`}
                onClick={(e) => e.stopPropagation()}
                className="text-jjblue hover:text-burgundy hover:underline"
              >
                {r.reporter_name || "a member"}
              </Link>
            </span>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-[var(--jj-muted)] shrink-0" /> : <ChevronDown className="h-4 w-4 text-[var(--jj-muted)] shrink-0" />}
      </button>

      {open && (
        <div className="mt-4 pl-12 space-y-4">
          {/* Concern chips */}
          {(r.concern_types || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {r.concern_types.map((c) => (
                <span key={c} className="text-[11px] bg-cream-surface border border-jj rounded-full px-2 py-0.5 text-jjblue/85">
                  {CONCERN_LABEL[c] || c}
                </span>
              ))}
            </div>
          )}

          {/* Description */}
          <div className="rounded-lg bg-cream-surface border border-jj p-3 text-sm">
            <div className="text-[11px] uppercase tracking-wider text-[var(--jj-muted)] mb-1.5">What's happening</div>
            <p className="text-jjblue/85 whitespace-pre-wrap break-words" data-testid={`elder-report-desc-${r.id}`}>{r.description}</p>
          </div>

          {/* Sensitive contact card */}
          <div className="rounded-lg border border-burgundy/30 bg-burgundy/5 p-3 text-sm">
            <div className="text-[11px] uppercase tracking-wider text-burgundy mb-1.5 flex items-center gap-1.5">
              <ShieldAlert className="h-3 w-3" /> Sensitive · admin only
            </div>
            <dl className="text-xs text-jjblue/85 grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
              {r.elder_contact && (
                <>
                  <dt className="text-[var(--jj-muted)] uppercase tracking-wider">Elder contact</dt>
                  <dd className="break-words" data-testid={`elder-report-contact-${r.id}`}>{r.elder_contact}</dd>
                </>
              )}
              <dt className="text-[var(--jj-muted)] uppercase tracking-wider">Reporter email</dt>
              <dd className="break-words">
                <a href={`mailto:${r.reporter_email}`} className="text-burgundy hover:underline">{r.reporter_email || "—"}</a>
              </dd>
              {r.reporter_phone && (
                <>
                  <dt className="text-[var(--jj-muted)] uppercase tracking-wider">Reporter phone</dt>
                  <dd>
                    <a href={`tel:${r.reporter_phone.replace(/[^\d+]/g, "")}`} className="text-burgundy hover:underline inline-flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {r.reporter_phone}
                    </a>
                  </dd>
                </>
              )}
              <dt className="text-[var(--jj-muted)] uppercase tracking-wider">OK to contact?</dt>
              <dd>{r.reporter_can_be_contacted ? "Yes" : "No — do not reach out"}</dd>
            </dl>
          </div>

          {/* Admin controls */}
          <div className="rounded-lg border border-jj bg-white p-3">
            <div className="grid sm:grid-cols-[auto,1fr,auto] gap-2 items-start">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[var(--jj-muted)]">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="input-jj mt-1 !py-1.5 text-xs"
                  data-testid={`elder-report-status-select-${r.id}`}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="min-w-0">
                <label className="text-[11px] uppercase tracking-wider text-[var(--jj-muted)]">Admin notes (private)</label>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={4000}
                  placeholder="Who's following up, what was tried, when to circle back…"
                  className="input-jj mt-1 text-sm"
                  data-testid={`elder-report-notes-${r.id}`}
                />
              </div>
              <button
                onClick={save}
                disabled={busy || !dirty}
                className="btn-primary !py-2 !px-3 text-xs inline-flex items-center gap-1.5 disabled:opacity-40 self-end"
                data-testid={`elder-report-save-${r.id}`}
              >
                <Save className="h-3.5 w-3.5" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * Admin-only Love Thy Elder reports panel. Mounted on /profile for admins.
 * Hooked to GET /api/admin/elder-reports and PATCH /api/admin/elder-reports/:id.
 */
export default function AdminElderReportsPanel() {
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (s = status) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/elder-reports", { params: { status: s, limit: 300 } });
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load elder reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(status); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);

  const openCount = useMemo(() => rows.filter((r) => r.status === "open").length, [rows]);
  const urgentCount = useMemo(() => rows.filter((r) => r.urgency === "immediate" && r.status !== "resolved").length, [rows]);

  const updateRow = (next) =>
    setRows((rs) => {
      // If we're filtered by status and the new status no longer matches, drop it
      if (status !== "all" && next.status !== status) {
        return rs.filter((r) => r.id !== next.id);
      }
      return rs.map((r) => (r.id === next.id ? next : r));
    });

  return (
    <div className="jj-card mt-6 p-6 sm:p-7 border-burgundy/30 bg-gradient-to-br from-cream to-cream-surface" data-testid="admin-elder-reports-panel">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="max-w-xl">
          <div className="overline text-burgundy flex items-center gap-2">
            <HandHeart className="h-3.5 w-3.5" /> Admin · Love Thy Elder
          </div>
          <h2 className="font-serif text-2xl text-jjblue mt-1" data-testid="admin-elder-reports-heading">
            {loading ? "Loading…" :
              urgentCount > 0
                ? `${urgentCount} urgent · ${openCount} open`
                : openCount > 0
                  ? `${openCount} open concern${openCount === 1 ? "" : "s"}.`
                  : "No open concerns."}
          </h2>
          <p className="mt-1.5 text-sm text-[var(--jj-text-secondary)]">
            Sensitive elder reports filed by members. Only admins see this section. Move cases through
            Open → In progress → Resolved as you act on them.
          </p>
        </div>

        <div className="inline-flex bg-cream-surface rounded-full p-1 text-xs" data-testid="admin-elder-reports-tabs">
          {[
            { k: "open", label: "Open" },
            { k: "in_progress", label: "In progress" },
            { k: "resolved", label: "Resolved" },
            { k: "all", label: "All" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setStatus(t.k)}
              className={`px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${status === t.k ? "bg-burgundy text-cream" : "text-jjblue/70"}`}
              data-testid={`admin-elder-reports-tab-${t.k}`}
            >
              <Filter className="h-3 w-3" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="text-center text-[var(--jj-muted)] py-8 text-sm">Loading reports…</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-[var(--jj-muted)] py-8 text-sm" data-testid="admin-elder-reports-empty">
            {status === "open" ? "Nothing open right now." : "Nothing matches this filter."}
          </div>
        ) : (
          <ul className="space-y-2" data-testid="admin-elder-reports-list">
            {rows.map((r) => (
              <ReportCard key={r.id} r={r} onUpdate={updateRow} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
